import { DndContext, DragEndEvent, closestCenter, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box } from '@mui/material';
import { FC, ReactNode, memo, useCallback, useMemo } from 'react';
import { ShiftTruckRowInfo } from 'src/components/dispatch/shiftTruckRowInfo';
import { DRAG_HANDLE_WIDTH, VEHICLE_LIST_GROUP_PADDING_Y } from 'src/components/dispatch/vehicleCardStyles';
import { LoadTotals, ZERO_LOAD } from 'src/components/dispatch/vehicleLoad';
import { ExpandedRowVM } from 'src/components/dispatch/vehicleRowExpand/buildExpandedRowVM';

import { RotationRow, VehicleRowGroup } from './adapter';
import { VehicleTariffTotals } from './buildGrossProfitByTruckId';
import { VehicleListRow, VehicleListRowProps } from './VehicleListRow';

import type { DraggableAttributes } from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';

// 静的な modifier。レンダリングのたびに新しい配列を生成しないようコンポーネント外で定義する。
// 親 (ConvenienceDispatchVehicleList) の外側 DndContext も同じ理由で自前に持つ。
const dndModifiers = [restrictToVerticalAxis];

/**
 * 行ごとに Map / Set から引いた表示状態。
 *
 * 親でグループ分だけ解決して配列で渡す。Set / Map をグループへ素通しすると、
 * どれか 1 つの参照が変わるだけで全グループが描き直される。
 */
export type VehicleListRowState = {
  selected: boolean;
  expanded: boolean;
  newlyAssigned: boolean;
  fixed: boolean;
  constraintAlerts?: readonly string[];
  expandedVM: ExpandedRowVM | null;
  driverName?: string;
  shiftTruckRowInfo?: ShiftTruckRowInfo;
  barColor?: string;
  displayRotationNumber: number;
  courseNumber?: string;
  noPlan: boolean;
  rowDeliveryId: number;
  newlyAssignedOrderIds?: ReadonlySet<number>;
  /** この行で選択できる停車地。index 由来なので選択では変わらない */
  selectableStopIds?: ReadonlySet<string>;
  /** この行で選択中の停車地。選択のある行だけ Set が入る */
  selectedStopIds?: ReadonlySet<string>;
};

export type VehicleListGroupProps = {
  /** このグループ。勤務 (delivery) 単位なので、同じ車両のグループが複数並ぶことがある */
  group: VehicleRowGroup;
  /** 並べ替えプレビュー適用後の行。無ければ group.rows と同一参照 */
  rows: RotationRow[];
  /**
   * グループの識別子 (vehicleRowGroupKey)。車両並べ替えの dnd-kit id と
   * 回転並べ替えプレビューのキーを兼ねる
   */
  sortableKey: string;
  /** vehicle.id から引いた truck。引けない (null) 車両は並べ替え対象外 */
  truckId: number | null;
  isLastGroup: boolean;
  /** 車両の並べ替えができない状況 (複数日表示・保存中) */
  vehicleSortDisabled: boolean;
  /**
   * 回転を並べ替えたときに呼ぶ。未指定なら並べ替えできない（ハンドルも出さない）。
   * どのグループの操作かを引数で渡すのは、親が全グループで 1 つの参照を使い回すため
   * （グループごとに作ると毎描画で参照が変わり、下の memo が効かない）。
   */
  onRotationDragEnd?: (
    groupKey: string,
    shiftTruckId: number | null,
    rows: RotationRow[],
    event: DragEndEvent,
  ) => void;
  sensors: ReturnType<typeof useSensors>;
  /** 行へ素通しする prop 群。親で useMemo して参照を固定する */
  rowProps: Omit<VehicleListRowProps,
    'row' | 'vehicle' | 'load' | 'rowIndex' | 'rowCount' | 'groupShiftTruckId' | 'tariffTotals'
    | 'rotationSortableId' | 'rotationDraggable' | 'vehicleDragHandle'
    | 'selected' | 'expanded' | 'newlyAssigned' | 'fixed' | 'constraintAlerts'
    | 'expandedVM' | 'driverName' | 'shiftTruckRowInfo' | 'barColor'
    | 'displayRotationNumber' | 'courseNumber' | 'noPlan' | 'rowDeliveryId' | 'newlyAssignedOrderIds'
    | 'selectableStopIds' | 'selectedStopIds'>;
  /**
   * このグループの行ごとの状態（rows と同じ並び）。
   * 配列は毎回作り直されるため、memo には要素ごとの浅い比較を持たせる。
   */
  rowStates: readonly VehicleListRowState[];
  /** 車両合計の運賃・粗利。isFirstGroupOfTruck の判定は親で済ませてある */
  tariffTotals: VehicleTariffTotals | null;
  /** 回転行ごとの積載量。絞り込み前の全グループから親が 1 度だけ作る */
  peakLoadByRowId: ReadonlyMap<string, LoadTotals>;
};

type SortableVehicleGroupProps = {
  /** 並べ替えの識別子。勤務単位 (`shift-{shiftTruckId}`) なので string */
  id: string;
  disabled: boolean;
  /** ハンドルはカード内に置くため、要素を受け取って好きな位置に描く */
  children: (dragHandle: ReactNode) => ReactNode;
};

/**
 * truck 単位の vertical sortable item。
 *
 * 掴めるのは左端のハンドルだけ。カード全体を掴めるようにすると、行選択・回転追加・
 * 色ピッカーなど中の操作のたびにドラッグが始まってしまう。
 */
const SortableVehicleGroup: FC<SortableVehicleGroupProps> = ({ id, disabled, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id, disabled });

  // ハンドルはカードの中（VehicleInfoView の左端）に置くので、要素を作って子へ渡す。
  // ここで描くとカードの外に出てしまい、行の背景と分離する。
  //
  // Why useMemo: 要素を毎回作り直すと、受け取った先頭行の props (vehicleDragHandle) の
  // 参照が毎回変わって行の memo が破れる。useSortable の attributes / listeners は
  // dnd-kit 側で memo 済み（@dnd-kit/core の useDraggable / useSyntheticListeners を確認）
  const handle = useMemo(
    () => <VehicleDragHandle disabled={disabled} attributes={attributes} listeners={listeners} />,
    [disabled, attributes, listeners],
  );

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      sx={{ display: 'flex', alignItems: 'stretch' }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>{children(handle)}</Box>
    </Box>
  );
};

type DragHandleProps = {
  disabled?: boolean;
  /** useSortable の戻り値をそのまま渡す。掴めないときは undefined */
  attributes?: DraggableAttributes;
  listeners?: SyntheticListenerMap;
};

/**
 * 並び替えハンドル。並び替えできない状況（複数日表示・並び替え不可の車両）でも
 * 場所は空けておく。消すと行頭の位置が車両ごとに変わって画面が跳ねる。
 */
const VehicleDragHandle: FC<DragHandleProps> = ({ disabled, attributes, listeners }) => (
  <Box
    component="button"
    type="button"
    aria-label="車両の並び替え"
    disabled={disabled}
    {...(disabled ? {} : attributes)}
    {...(disabled ? {} : listeners)}
    sx={{
      flexShrink: 0,
      width: DRAG_HANDLE_WIDTH,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      p: 0,
      bgcolor: 'transparent',
      color: 'hsl(var(--muted-foreground))',
      opacity: disabled ? 0.25 : 0.6,
      cursor: disabled ? 'default' : 'grab',
      '&:active': disabled ? undefined : { cursor: 'grabbing' },
      '&:hover': disabled ? undefined : { opacity: 1 },
    }}
  >
    <DragIndicatorIcon sx={{ fontSize: 14 }} />
  </Box>
);

/**
 * 掴めない車両のハンドル。要素を使い回すのは、毎回作り直すと受け取った先頭行の
 * props (vehicleDragHandle) の参照が変わって行の memo が破れるため。
 */
const disabledVehicleDragHandle = <VehicleDragHandle disabled />;

/**
 * 回転行の DnD 有効範囲。並べ替えない画面では素通しし、DOM を増やさない
 * (convenience 画面の既存構造を変えないため)。
 *
 * Why 入れ子の DndContext: 外側は車両の並び替えを持っている。同じ context に
 * 回転行を混ぜると、車両 id と行 id が同じ items に並んで落とし先が入り乱れる。
 */
const RotationSortableScope: FC<{
  enabled: boolean;
  sensors: ReturnType<typeof useSensors>;
  rowIds: string[];
  onDragEnd: (event: DragEndEvent) => void;
  children: ReactNode;
}> = ({ enabled, sensors, rowIds, onDragEnd, children }) => {
  if (!enabled) return <>{children}</>;
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={dndModifiers}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
};

const VehicleListGroupInner: FC<VehicleListGroupProps> = ({
  group,
  rows,
  sortableKey,
  truckId,
  isLastGroup,
  vehicleSortDisabled,
  onRotationDragEnd,
  sensors,
  rowProps,
  rowStates,
  tariffTotals,
  peakLoadByRowId,
}) => {
  // Why useMemo: SortableContext は items の参照を context 値の deps に入れる
  // (@dnd-kit/sortable 8.0.0 の sortable.cjs.development.js:315 / 331-342)。
  // 毎回新しい配列を渡すと context 値が毎描画で変わり、useSortable を呼ぶ行が
  // memo を無視して描き直される
  const rowIds = useMemo(() => rows.map((it) => it.rowId), [rows]);
  // 回転が 1 行だけの車両は並べ替えても何も変わらない
  const rotationSortable = !!onRotationDragEnd && truckId != null && rows.length > 1;
  const handleRotationDragEnd = useCallback(
    (event: DragEndEvent) => { onRotationDragEnd?.(sortableKey, group.shiftTruckId, rows, event); },
    [onRotationDragEnd, sortableKey, group.shiftTruckId, rows],
  );

  // ハンドルはカード内に置くので、外から要素を受け取って各行へ渡す
  const groupContent = (dragHandle: ReactNode) => (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1px',
          bgcolor: 'hsl(var(--secondary) / 0.2)',
          px: 1,
          // 元は `py: 0.75`（テーマの spacing は 9 なので 6.75px）。同じ値を定数からも
          // 読めるようにする。横にレーンを並べる画面がグループの外形高さを要るため
          py: `${VEHICLE_LIST_GROUP_PADDING_Y}px`,
        }}
      >
        <RotationSortableScope
          enabled={rotationSortable}
          sensors={sensors}
          rowIds={rowIds}
          onDragEnd={handleRotationDragEnd}
        >
          {rows.map((row, rowIndex) => (
            <VehicleListRow
              key={`${row.rowId}-row`}
              {...rowProps}
              {...rowStates[rowIndex]}
              row={row}
              vehicle={group.vehicle}
              load={peakLoadByRowId.get(row.rowId) ?? ZERO_LOAD}
              rowIndex={rowIndex}
              rowCount={rows.length}
              groupShiftTruckId={group.shiftTruckId}
              // 車両合計なので 1 回転目の行にだけ渡す。全行に出すと
              // 同じ数字が並び「回転ごとの値」と誤読される。
              tariffTotals={rowIndex === 0 ? tariffTotals : null}
              rotationSortableId={rotationSortable ? row.rowId : undefined}
              // 1 行目は車両の並び替えハンドルが行頭を使っているので掴めない
              // （落とし先にはなるので、2 行目を 1 行目の上へ落とせば先頭にできる）
              rotationDraggable={rowIndex > 0}
              // 車両ハンドルはカード内に置くので、グループが受け取った要素を先頭行へ渡す
              vehicleDragHandle={rowIndex === 0 ? dragHandle : undefined}
            />
          ))}
        </RotationSortableScope>
      </Box>
      {!isLastGroup && <Box sx={{ mx: 1, borderBottom: '1px solid hsl(var(--border) / 0.4)' }} />}
    </>
  );

  // truckId が null の (= 不正な vehicle.id) 場合は sortable 対象外。
  // ハンドルの場所だけは空けて、他の車両と行頭を揃える
  if (truckId == null) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>{groupContent(disabledVehicleDragHandle)}</Box>
      </Box>
    );
  }
  return (
    <SortableVehicleGroup id={sortableKey} disabled={vehicleSortDisabled}>
      {groupContent}
    </SortableVehicleGroup>
  );
};

/** 行の状態は配列の中身を見る。親が毎回作り直すので参照比較では効かない */
const equalRowStates = (
  a: readonly VehicleListRowState[],
  b: readonly VehicleListRowState[],
): boolean => a.length === b.length && a.every((x, i) => {
  const y = b[i];
  // Why not フィールドを 1 つずつ書き出す: フィールドを足して比較を書き忘れると、
  // その値が変わってもグループが bail-out して行が新しい props を受け取らない
  // （「マスタを直したのに表示が変わらない」側へ静かに倒れる。行側の memo では
  // 守れない。グループが描かれなければ行の要素すら作られないため）。
  // keys を回せば漏れても「余分に描く」側に倒れる。
  //
  // 片方にしか無い key も見るため両方を展開する。VehicleListRowState は
  // 全フィールドが === で足りる（比較が要る参照はすべて親で Map から引いた値）
  return (Object.keys({ ...x, ...y }) as (keyof VehicleListRowState)[])
    .every((k) => x[k] === y[k]);
});

/**
 * rowStates だけ中身を比べ、残りは keys を回して浅く比較する。
 *
 * Why not prop を 1 つずつ書き出す: prop を足したときに比較を書き忘れると、その prop が
 * 変わっても再描画されない = 表示が更新されない側に静かに倒れる。keys を回せば
 * 新しい prop も自動で比較対象に入り、漏れても「余分に描く」側に倒れる。
 *
 * Why not rowStates も参照比較で済ます: 親が毎描画で作り直す配列なので、参照比較では
 * 常に false になり memo が効かない。中身を見ないなら Set / Map をグループへ
 * 素通しすることになり、どれか 1 つの参照が変わるだけで全グループが描き直される。
 */
export const areVehicleListGroupPropsEqual = (
  prev: VehicleListGroupProps,
  next: VehicleListGroupProps,
): boolean => {
  if (!equalRowStates(prev.rowStates, next.rowStates)) return false;
  const keys = Object.keys({ ...prev, ...next }) as (keyof VehicleListGroupProps)[];
  return keys.every((k) => k === 'rowStates' || prev[k] === next[k]);
};

/**
 * 車両リストの 1 グループ（= 1 勤務）。memo 境界。
 *
 * Why not 親で組む: グループの map が毎描画ですべての行の props を組み直すため、
 * 行を memo 境界にしても、選択に関係しないグループの行まで props の参照が変わって
 * 描き直されていた。行ごとの状態は親で解決して配列で受け取り、要素ごとの浅い比較で
 * 「自分の行の状態が変わったときだけ」描き直す。
 */
export const VehicleListGroup: FC<VehicleListGroupProps> = memo(
  VehicleListGroupInner,
  areVehicleListGroupPropsEqual,
);

VehicleListGroup.displayName = 'VehicleListGroup';
